export interface DashboardFilterOption {
  code: string;
  name: string;
  label?: string;
  category?: string;
}

export interface DashboardFilterSelection {
  selected: string[];
  exclude: boolean;
}

export interface DashboardFilterState {
  location: DashboardFilterSelection;
  category: DashboardFilterSelection;
  class: DashboardFilterSelection;
}

export interface DashboardFilterRequestParams {
  locations: string[];
  categories: string[];
  classes: string[];
  locationExclude: boolean;
  categoryExclude: boolean;
  classExclude: boolean;
}

export function createEmptyDashboardFilterSelection(): DashboardFilterSelection {
  return { selected: [], exclude: false }
}

export function createEmptyDashboardFilters(): DashboardFilterState {
  return {
    location: createEmptyDashboardFilterSelection(),
    category: createEmptyDashboardFilterSelection(),
    class: createEmptyDashboardFilterSelection(),
  }
}

export function normalizeDashboardFilterSelection(selection?: Partial<DashboardFilterSelection>): DashboardFilterSelection {
  const selected = Array.isArray(selection?.selected)
    ? Array.from(new Set(selection?.selected.filter((value): value is string => Boolean(value && String(value).trim()))))
    : []

  return {
    selected,
    exclude: selected.length > 0 ? Boolean(selection?.exclude) : false,
  }
}

export function normalizeDashboardFilters(filters?: Partial<DashboardFilterState>): DashboardFilterState {
  return {
    location: normalizeDashboardFilterSelection(filters?.location),
    category: normalizeDashboardFilterSelection(filters?.category),
    class: normalizeDashboardFilterSelection(filters?.class),
  }
}

export function buildDashboardFilterRequestParams(filters: DashboardFilterState): DashboardFilterRequestParams {
  const normalized = normalizeDashboardFilters(filters)

  return {
    locations: normalized.location.selected,
    categories: normalized.category.selected,
    classes: normalized.class.selected,
    locationExclude: normalized.location.exclude,
    categoryExclude: normalized.category.exclude,
    classExclude: normalized.class.exclude,
  }
}

export function serializeDashboardFilters(filters: DashboardFilterState): string {
  const normalized = normalizeDashboardFilters(filters)

  return JSON.stringify({
    location: {
      selected: [...normalized.location.selected].sort(),
      exclude: normalized.location.exclude,
    },
    category: {
      selected: [...normalized.category.selected].sort(),
      exclude: normalized.category.exclude,
    },
    class: {
      selected: [...normalized.class.selected].sort(),
      exclude: normalized.class.exclude,
    },
  })
}

export function hasActiveDashboardFilters(filters: DashboardFilterState): boolean {
  const normalized = normalizeDashboardFilters(filters)

  return normalized.location.selected.length > 0
    || normalized.category.selected.length > 0
    || normalized.class.selected.length > 0
}

export function getDashboardFilterOptionLabel(option: DashboardFilterOption): string {
  return option?.label || (option?.code && option?.name ? `${option.code} - ${option.name}` : option?.name || option?.code || '')
}

export function getDashboardFilterSelectionLabel(
  selection: DashboardFilterSelection,
  options: DashboardFilterOption[],
  placeholder: string,
  pluralLabel?: string,
): string {
  const normalized = normalizeDashboardFilterSelection(selection)

  if (normalized.selected.length === 0) return placeholder

  if (normalized.selected.length === 1) {
    const matched = options.find((option) => option.code === normalized.selected[0])
    const singleLabel = matched ? getDashboardFilterOptionLabel(matched) : normalized.selected[0]
    return normalized.exclude ? `Exclude: ${singleLabel}` : singleLabel
  }

  const countLabel = pluralLabel || 'selected'
  return normalized.exclude
    ? `Exclude: ${normalized.selected.length} ${countLabel}`
    : `${normalized.selected.length} ${countLabel}`
}

export function getScopedDashboardClassOptions(
  classOptions: DashboardFilterOption[],
  categorySelection: DashboardFilterSelection,
): DashboardFilterOption[] {
  const normalizedCategorySelection = normalizeDashboardFilterSelection(categorySelection)

  if (normalizedCategorySelection.exclude || normalizedCategorySelection.selected.length === 0) {
    return classOptions
  }

  const selectedCategories = new Set(normalizedCategorySelection.selected)
  return classOptions.filter((option) => !option.category || selectedCategories.has(option.category))
}

